import { IActionsheetRefProps } from "@repo/core/components/layout/Actionsheet/types";
/* import { IModalRefProps } from "@repo/core/components/layout/Modal/types";
import { IModalOption } from "@repo/core/components/layout/ModalTemplate/types"; */
import { useDimensions } from "@repo/core/hooks/useDimensions";
import { useNavigation } from "@repo/core/hooks/useNavigation";
import { ROUTES } from "@repo/core/routes";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { BackHandler } from "react-native";
import { ValidationStep, getValidationStyleMap } from "./utils";
import { usePreProposal } from "../../hooks/usePreProposal";
import { useProposalStatusPolling } from "@repo/core/hooks/useProposalStatusPolling";
import { apiInstance } from "@repo/core/services/instances/api";
import { ProposalsResource } from "@repo/core/services/resources/proposals";
import {
  PROPOSTA_SITUACAO,
  TIPO_PESSOA,
  UpdatePreProposalInputDTO
} from "@repo/core/services/entities/Proposals";
import { useQueryClient } from "@tanstack/react-query";
import { useCreditProposal } from "@repo/core/screens/ProposalEffectiveFlows/Credit/hooks/useCreditProposal";
import { useFidelityProposal } from "@repo/core/screens/ProposalEffectiveFlows/Fidelity/hooks/useFidelityProposal";
import { ProposalTypeByCode } from "@repo/core/constants/proposals";
import { LimitReassessmentEnum } from "@repo/core/enums/proposals";
import { sanitizePreProposalApiPayload } from "../../services/sanitizePreProposalApiPayload.service";
import {
  shouldNavigateToCreditProposalFlow,
  navigateToReanalysisCreditFlowIfApplicable
} from "../BiometryValidation/services/navigation.service";

const proposalsResource = new ProposalsResource(apiInstance);

/** CEP placeholder when pre-proposal has no address yet (credit engine / SPC flow expects 8 digits in crtpropostas). */
const PRE_PROPOSAL_PLACEHOLDER_CEP = "00000000";

const resolveResidentialCepForAlterarPre = (proposal: unknown): string => {
  const p = proposal as
    | { strResCep?: string; strCep?: string }
    | null
    | undefined;
  const digits = (p?.strResCep || p?.strCep || "").replace(/\D/g, "");
  return digits.length === 8 ? digits : PRE_PROPOSAL_PLACEHOLDER_CEP;
};

export const VerifyInformationsController = () => {
  /*   const alertModalRef = useRef<IModalRefProps>(null); */
  const actionSheetRef = useRef<IActionsheetRefProps>(null);
  const { isTabletOrWeb } = useDimensions();
  const queryClient = useQueryClient();

  const { resetTo } = useNavigation();
  const { resetPreProposalStore, proposalData } = usePreProposal();
  const { clearCreditProposal } = useCreditProposal();
  const { clearFidelityProposal } = useFidelityProposal();

  const [status, setStatus] = useState<ValidationStep>(ValidationStep.AWAITING);
  const [shouldStartPolling, setShouldStartPolling] = useState(false);
  const hasSentProposalRef = useRef(false);
  const hasErrorRef = useRef(false);

  const isShowOptions = status !== ValidationStep.AWAITING;

  const shouldSkipPreProposalAnalysis =
    shouldNavigateToCreditProposalFlow(proposalData);

  const { isApproved, isRejected, isDerived } = useProposalStatusPolling({
    proposalId: proposalData?.lngProposta?.toString(),
    enabled:
      shouldStartPolling &&
      !!proposalData?.lngProposta &&
      !shouldSkipPreProposalAnalysis,
    pollingInterval: 3000,
    isReanalysis: shouldSkipPreProposalAnalysis,
    onStatusChange: (newStatus) => {
      console.log("VerifyInformations: Proposal status changed:", newStatus);
    },
    onApproved: () => {
      setStatus(ValidationStep.ELIGIBLE);
      queryClient.invalidateQueries({ queryKey: ["recentProposals"] });
      queryClient.invalidateQueries({ queryKey: ["proposalsSearch"] });
    },
    onRejected: () => {
      setStatus(ValidationStep.NOT_ELIGIBLE);
    },
    onDerived: () => {
      setStatus(ValidationStep.NOT_ELIGIBLE);
    }
  });

  const handleContinue = () => {
    if (status === ValidationStep.NOT_ELIGIBLE) {
      resetPreProposalStore();

      //Invalid queries to refresh the data
      queryClient.invalidateQueries({ queryKey: ["recentProposals"] });
      queryClient.invalidateQueries({ queryKey: ["proposalsSearch"] });

      // Navigate to fidelity proposal complete registration
      clearFidelityProposal();
      resetTo(ROUTES.fidelityProposalCompleteRegistration, {
        proposalId: proposalData?.lngProposta?.toString()
      });
    } else {
      queryClient.invalidateQueries({ queryKey: ["recentProposals"] });
      queryClient.invalidateQueries({ queryKey: ["proposalsSearch"] });

      // Navigate to credit proposal personal data
      clearCreditProposal();
      resetTo(ROUTES.creditProposalPersonalData, {
        proposalId: proposalData?.lngProposta?.toString()
      });
    }
  };

  const handleViewProposal = () => {
    const proposalId = proposalData?.lngProposta?.toString();

    if (!proposalId) {
      return;
    }

    queryClient.invalidateQueries({ queryKey: ["recentProposals"] });
    queryClient.invalidateQueries({ queryKey: ["proposalsSearch"] });

    resetTo(ROUTES.proposalsViewInfo, {
      proposalId
    });
  };

  const handleShowOptions = () => {
    actionSheetRef.current?.open();
  };

  const handleCloseOptions = () => {
    actionSheetRef.current?.close();
  };

  const buttonActions = useMemo(() => {
    switch (status) {
      case ValidationStep.ELIGIBLE:
        return {
          primary: {
            label: "Continuar",
            onPress: handleContinue
          }
        };
      case ValidationStep.NOT_ELIGIBLE:
        return {
          primary: {
            label: "Cadastrar Fidelidade",
            onPress: handleContinue
          },
          secondary: {
            label: "Opções",
            icon: "ellipsis-vertical",
            onPress: handleShowOptions
          }
        };
      default:
        return {
          primary: {
            label: "Voltar para as propostas",
            onPress: handleContinue
          }
        };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const validationInfo = useMemo(() => getValidationStyleMap[status], [status]);

  /*  const alertOptions: IModalOption[] = [
    {
      label: "Continuar",
      onPress: () => alertModalRef.current?.close()
    }
  ]; */

  const sendProposalToAnalyze = async () => {
    if (shouldNavigateToCreditProposalFlow(proposalData)) {
      return;
    }

    // Prevent multiple calls
    if (hasSentProposalRef.current || hasErrorRef.current) {
      return;
    }

    try {
      setStatus(ValidationStep.AWAITING);
      hasSentProposalRef.current = true;
      const sanitizedProposalData = sanitizePreProposalApiPayload(proposalData);

      await proposalsResource.editPreProposal({
        ...(sanitizedProposalData as unknown as UpdatePreProposalInputDTO),
        strResCep: resolveResidentialCepForAlterarPre(proposalData),
        // Fixed fields
        strSituacao: PROPOSTA_SITUACAO.PRE_PROPOSAL,
        blnManterPendente: false,
        strTipoProposta: ProposalTypeByCode.AMBOS,
        strTipoPessoa: TIPO_PESSOA.FISICA,
        strReavaliacaoLimite: LimitReassessmentEnum.ANALYSIS
      });

      setShouldStartPolling(true);
      hasErrorRef.current = false;
    } catch (error) {
      console.error(
        "VerifyInformations: Error sending proposal to analyze",
        error
      );
      hasErrorRef.current = true;
      hasSentProposalRef.current = false; // Allow retry if needed

      // Set error status to show error message to user
      setStatus(ValidationStep.NOT_ELIGIBLE);

      // Don't start polling if there was an error
      setShouldStartPolling(false);
    }
  };

  // Stop polling when proposal reaches final status (approved, rejected or derived)
  useEffect(() => {
    if (isApproved || isRejected || isDerived) {
      setShouldStartPolling(false);
    }
  }, [isApproved, isRejected, isDerived]);

  useLayoutEffect(() => {
    navigateToReanalysisCreditFlowIfApplicable(
      proposalData,
      resetTo,
      clearCreditProposal
    );
  }, [proposalData, resetTo, clearCreditProposal]);

  useEffect(() => {
    if (shouldSkipPreProposalAnalysis) {
      return;
    }

    if (!hasSentProposalRef.current && !hasErrorRef.current) {
      sendProposalToAnalyze();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    shouldSkipPreProposalAnalysis,
    proposalData?.lngProposta,
    proposalData?.strReavaliacaoLimite
  ]);

  useEffect(() => {
    const onBackPress = () => true; // avoid any action
    const subscription = BackHandler.addEventListener(
      "hardwareBackPress",
      onBackPress
    );
    return () => {
      subscription.remove();
    };
  }, []);

  return {
    isTabletOrWeb,
    validationInfo,
    status,
    /*     alertModalRef, */
    buttonActions,
    actionSheetRef,
    handleCloseOptions,
    /*     alertOptions, */
    isShowOptions,
    handleContinue,
    handleViewProposal
  };
};
